// Kleiner Lade-Hook für Listen und Detailseiten.
// Nimmt den drei Zuständen (lädt / Fehler / Tarifsperre) die Wiederholung ab,
// die sonst auf jeder Seite von Hand stünde.
import { useCallback, useEffect, useState } from 'react';
import { api, ApiFehler } from './api';

export type Ladezustand<T> = {
  daten: T | null;
  laedt: boolean;
  fehler: string | null;
  /** Gesetzt, wenn die Funktion nicht im Tarif enthalten ist (HTTP 402). */
  tarifsperre: { upgradeAuf?: string } | null;
  neuLaden: () => Promise<void>;
  setzen: (d: T) => void;
};

export function useDaten<T>(pfad: string, abhaengig: unknown[] = []): Ladezustand<T> {
  const [daten, setDaten] = useState<T | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [tarifsperre, setTarifsperre] = useState<{ upgradeAuf?: string } | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      setDaten(await api<T>(pfad));
      setFehler(null);
      setTarifsperre(null);
    } catch (e) {
      if (e instanceof ApiFehler && e.istTarifgrenze) {
        setTarifsperre({ upgradeAuf: e.daten?.upgradeTo as string | undefined });
      } else {
        setFehler(e instanceof ApiFehler ? e.message : 'Daten konnten nicht geladen werden.');
      }
    } finally {
      setLaedt(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pfad, ...abhaengig]);

  useEffect(() => { void laden(); }, [laden]);

  return { daten, laedt, fehler, tarifsperre, neuLaden: laden, setzen: setDaten };
}

// Währung und Schreibweise stammen aus dem Länderprofil des Betriebs.
//
// Bewusst als Modulzustand und nicht als React-Kontext: euro() und datum()
// werden an über hundert Stellen aufgerufen, oft außerhalb von Komponenten.
// Ein Kontext hätte jede dieser Stellen zu einem Hook gemacht. Das Profil
// ändert sich nur in den Einstellungen — dort wird nach dem Speichern neu
// geladen, damit alle Anzeigen sicher stimmen.
export type Profil = {
  locale: string;
  waehrung: string;
  steuersaetze: number[];
  standardSteuersatz: number;
  steuerName: string;
  steuerKurz: string;
  deutschesSteuerrecht: boolean;
  land?: string;
  code?: string;
};

const STANDARD_PROFIL: Profil = {
  locale: 'de-DE', waehrung: 'EUR', steuersaetze: [19, 7, 0], standardSteuersatz: 19,
  steuerName: 'Umsatzsteuer', steuerKurz: 'USt.', deutschesSteuerrecht: true,
};

let aktivesProfil: Profil = STANDARD_PROFIL;

export function profilSetzen(p: Partial<Profil> | null | undefined) {
  aktivesProfil = { ...STANDARD_PROFIL, ...(p || {}) };
}

export function profil(): Profil {
  return aktivesProfil;
}

export const euro = (n: number | null | undefined) =>
  (Number(n) || 0).toLocaleString(aktivesProfil.locale, {
    style: 'currency', currency: aktivesProfil.waehrung,
  });

/**
 * Preis je Einheit — bis zu vier Nachkommastellen. 0,012 €/g oder 0,2945 €/kWh
 * wären mit `euro` auf 0,01 € und 0,29 € geschrumpft. Glatte Preise bleiben
 * bei zwei Stellen: 12,50 €, nicht 12,5000 €.
 */
export const preis = (n: number | null | undefined) =>
  (Number(n) || 0).toLocaleString(aktivesProfil.locale, {
    style: 'currency', currency: aktivesProfil.waehrung,
    minimumFractionDigits: 2, maximumFractionDigits: 4,
  });

export const datum = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleDateString(aktivesProfil.locale) : '—';

export const datumZeit = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleString(aktivesProfil.locale, { dateStyle: 'short', timeStyle: 'short' }) : '—';

export const menge = (n: number, einheit = '') =>
  `${Number(n).toLocaleString(aktivesProfil.locale, { maximumFractionDigits: 3 })}${einheit ? ' ' + einheit : ''}`;
